/**
 * Form Helpers - Utility functions for form field value normalization
 * 
 * These helpers extract common branching logic from form components to improve
 * code reusability, testability, and branch coverage metrics.
 */

/**
 * Normalizes a selector value that can be either a string or string array
 * Returns the first element if array, or the original value, with empty string fallback
 * 
 * @param value - The value from a Selector component (string | string[])
 * @returns Normalized string value, empty string if falsy
 * 
 * @example
 * normalizeSelectValue(['option1', 'option2']) // 'option1'
 * normalizeSelectValue('option1') // 'option1'
 * normalizeSelectValue([]) // ''
 * normalizeSelectValue('') // ''
 * normalizeSelectValue(null) // ''
 */
export const normalizeSelectValue = (value: string | string[] | null | undefined): string => {
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  return value || '';
};

/**
 * Normalizes a selector value to a numeric value with optional parsing
 * Useful for ID fields that come as strings but need to be numbers
 * 
 * @param value - The value from a Selector component (string | string[])
 * @returns Parsed number or undefined if value is falsy
 * 
 * @example
 * normalizeNumericSelectValue(['13']) // 13
 * normalizeNumericSelectValue('13') // 13
 * normalizeNumericSelectValue('') // undefined
 * normalizeNumericSelectValue([]) // undefined
 * normalizeNumericSelectValue(null) // undefined
 */
export const normalizeNumericSelectValue = (
  value: string | string[] | null | undefined,
): number | undefined => {
  const stringValue = Array.isArray(value) ? value[0] : value;
  return stringValue ? parseInt(stringValue, 10) : undefined;
};

/**
 * Normalizes a selector value to a number with 0 as fallback
 * Useful for required numeric fields that should never be undefined
 * 
 * @param value - The value from a Selector component (string | string[])
 * @returns Parsed number or 0 if value is falsy
 * 
 * @example
 * normalizeSelectToNumber(['5']) // 5
 * normalizeSelectToNumber('5') // 5
 * normalizeSelectToNumber('') // 0
 * normalizeSelectToNumber([]) // 0
 * normalizeSelectToNumber(null) // 0
 */
export const normalizeSelectToNumber = (value: string | string[] | null | undefined): number => {
  const stringValue = Array.isArray(value) ? value[0] : value;
  return stringValue ? Number(stringValue) : 0;
};

/**
 * Normalizes a selector value with a custom fallback value
 * Useful for fields with specific default values (e.g., 'V' for employmentStatus)
 * 
 * @param value - The value from a Selector component (string | string[])
 * @param fallback - The fallback value to use when value is falsy
 * @returns Normalized string value or fallback
 * 
 * @example
 * normalizeSelectWithFallback(['A'], 'V') // 'A'
 * normalizeSelectWithFallback('', 'V') // 'V'
 * normalizeSelectWithFallback([], 'ACTIVE') // 'ACTIVE'
 * normalizeSelectWithFallback(null, 'DEFAULT') // 'DEFAULT'
 */
export const normalizeSelectWithFallback = (
  value: string | string[] | null | undefined,
  fallback: string,
): string => {
  if (Array.isArray(value)) {
    return value[0] || fallback;
  }
  return value || fallback;
};

/**
 * Normalizes a watch value with optional or undefined fallback
 * Useful for DatePicker and other components that accept undefined
 * 
 * @param value - The value from form.watch()
 * @returns Original value or undefined if falsy
 * 
 * @example
 * normalizeWatchValue('2024-01-01') // '2024-01-01'
 * normalizeWatchValue('') // undefined
 * normalizeWatchValue(null) // undefined
 */
export const normalizeWatchValue = <T>(
  value: T | null | undefined,
): T | undefined => value || undefined;

/**
 * Normalizes a watch value with empty string fallback
 * Useful for LabelInput and other text inputs
 * 
 * @param value - The value from form.watch()
 * @returns Original value or empty string if falsy
 * 
 * @example
 * normalizeWatchValueToString('John') // 'John'
 * normalizeWatchValueToString(null) // ''
 * normalizeWatchValueToString(undefined) // ''
 */
export const normalizeWatchValueToString = (value: string | null | undefined): string => value || '';
